// Convierte una entrada de audit_log en una frase legible en español.
// Cubre los casos que aparecen en NotificationBell, ChangesPopup y
// PendientesRevisionPage para mantener la voz consistente.

// Etiqueta corta por acción. Se usa como cabecera ("¿qué pasó?").
export const ACTION_LABELS = {
  'asset.create': 'Activo creado',
  'asset.update': 'Activo modificado',
  'asset.delete': 'Activo eliminado',
  'asset.quick_update': 'Actualización rápida',
  'asset.assign': 'Usuario asignado',
  'asset.unassign': 'Usuario desasignado',
  'asset.publish': 'Activo publicado',
  'asset.unpublish': 'Activo despublicado',
  'asset.trash': 'Activo enviado a papelera',
  'asset.purge': 'Activo purgado',
  'asset.bulk_estado': 'Cambio de estado masivo',
  'asset.bulk_prioridad': 'Cambio de prioridad masivo',
  'asset.bulk_restore': 'Activos restaurados',
  'asset.pdf_import': 'Activo importado (PDF)',
  'asset.note.create': 'Nota añadida',
  'asset.interaction.create': 'Interacción registrada',
  'asset.document.upload': 'Documento subido',
  'asset.balance.update': 'Balance actualizado',
  'asset.mediation.update': 'Mediación actualizada',
  'asset.review_flag': 'Revisión del activo',
  'document.print_preview': 'Documento previsualizado/impreso',
  'import.excel': 'Importación Excel',
  'import.merge.deuda': 'Deuda fusionada',
  'user.create': 'Usuario creado',
  'user.role.change': 'Cambio de rol',
};

// Nombre amigable de cada campo del activo.
const FIELD_LABELS = {
  precio_compra: 'Precio compra',
  precio_compra_estimado: 'Precio compra (estimado)',
  valor_salida_individual: 'Valor de salida',
  valor_salida_base: 'Valor de salida base',
  tasacion: 'Tasación',
  metros: 'Metros cuadrados',
  direccion: 'Dirección',
  municipio: 'Municipio',
  provincia: 'Provincia',
  estado: 'Estado',
  fase: 'Fase',
  fase_judicial: 'Fase judicial',
  estado_procesal: 'Estado procesal',
  prioridad: 'Prioridad',
  semaforo: 'Semáforo',
  accion_recomendada: 'Acción recomendada',
  tipo_activo: 'Tipo de inmueble',
  tipo_activo_operativo: 'Tipo operativo',
  posesion: 'Posesión',
  proveedor: 'Proveedor',
  id_proveedor: 'ID proveedor',
  deuda_total: 'Deuda total',
  deuda_reclamada: 'Deuda reclamada',
  referencia_catastral: 'Referencia catastral',
  roi_rapido: 'ROI rápido',
  score_premediacion: 'Score económico',
  score_financiero: 'Score financiero',
  score_total: 'Score total',
  es_top: 'Top oportunidad',
  publicado: 'Publicado',
  published: 'Publicado',
  is_public: 'Visible al inversor',
  notas_internas: 'Notas internas',
  observaciones: 'Observaciones',
  descarte_motivos: 'Motivos de descarte',
  gestores: 'Gestores',
  inversores: 'Inversores',
  comprador: 'Comprador',
  fecha_compra: 'Fecha de compra',
  vivienda_habitual: 'Vivienda habitual',
};

const MONEY_FIELDS = new Set([
  'precio_compra', 'precio_compra_estimado',
  'valor_salida_individual', 'valor_salida_base',
  'tasacion', 'deuda_total', 'deuda_reclamada',
]);
const M2_FIELDS = new Set(['metros']);
const PERCENT_FIELDS = new Set(['roi_rapido']);
const BOOLEAN_FIELDS = new Set(['es_top', 'publicado', 'published', 'is_public', 'vivienda_habitual']);

const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
};
const fmtNumber = (v, suffix = '') => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 }).format(n)}${suffix}`;
};
const fmtBoolean = (v) => (v === true || v === 'true' ? 'Sí' : v === false || v === 'false' ? 'No' : '—');
const fmtArray = (v) => {
  if (!Array.isArray(v)) return v == null ? '—' : String(v);
  if (v.length === 0) return '(vacío)';
  if (v.length <= 3) return v.join(', ');
  return `${v.length} elementos`;
};
const fmtPlain = (v) => {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return fmtArray(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const formatFieldValue = (field, value) => {
  if (MONEY_FIELDS.has(field)) return fmtMoney(value);
  if (M2_FIELDS.has(field)) return fmtNumber(value, ' m²');
  if (PERCENT_FIELDS.has(field)) return fmtNumber(value, ' %');
  if (BOOLEAN_FIELDS.has(field)) return fmtBoolean(value);
  if (field === 'gestores' || field === 'inversores' || field === 'descarte_motivos') return fmtArray(value);
  return fmtPlain(value);
};

const labelForField = (field) => FIELD_LABELS[field] || field.replace(/_/g, ' ');

// Describe un único par {field: {from, to}} en una frase corta.
const describeFieldChange = (field, change) => {
  const label = labelForField(field);
  const from = formatFieldValue(field, change?.from);
  const to = formatFieldValue(field, change?.to);
  if (from === '—' || from === '(vacío)') return `${label}: ahora ${to}`;
  if (to === '—' || to === '(vacío)') return `${label}: se borró (era ${from})`;
  return `${label}: ${from} → ${to}`;
};

// Devuelve un array de líneas describiendo los cambios. Si no hay info
// de detalle, devuelve un array vacío y la cabecera ya cuenta la historia.
export const describeChangeDetails = (log) => {
  if (!log) return [];
  const action = log.action || '';
  const meta = log.meta || {};

  if (action === 'asset.update' || action === 'asset.quick_update') {
    const changes = meta.changes || {};
    const keys = Object.keys(changes);
    if (keys.length === 0) return [];
    return keys.slice(0, 6).map((k) => describeFieldChange(k, changes[k]))
      .concat(keys.length > 6 ? [`… y ${keys.length - 6} cambios más`] : []);
  }

  if (action === 'asset.create' || action === 'asset.pdf_import') {
    const parts = [];
    if (meta.codigo_npl) parts.push(`NPL ${meta.codigo_npl}`);
    if (meta.proveedor) parts.push(`Proveedor: ${meta.proveedor}`);
    if (meta.tipo_activo_operativo) parts.push(`Tipo: ${meta.tipo_activo_operativo}`);
    if (meta.precio_compra != null) parts.push(`Precio compra: ${fmtMoney(meta.precio_compra)}`);
    return parts;
  }

  if (action === 'asset.assign') {
    const who = meta.user_email || meta.user_id;
    const field = meta.field === 'inversores' ? 'inversor' : 'gestor';
    return who ? [`Asignado como ${field}: ${who}`] : [];
  }
  if (action === 'asset.unassign') {
    const who = meta.user_email || meta.user_id;
    return who ? [`Desasignado: ${who}`] : [];
  }

  if (action === 'asset.trash') {
    const prev = meta.estado_anterior ? ` (estaba en ${meta.estado_anterior})` : '';
    return [`Enviado a papelera${prev}`];
  }
  if (action === 'asset.purge') {
    return ['Eliminado definitivamente'];
  }
  if (action === 'asset.publish') {
    return meta.mensaje ? [`Mensaje: ${meta.mensaje}`] : ['Visible para inversores asignados'];
  }
  if (action === 'asset.unpublish') {
    return ['Dejó de ser visible para inversores'];
  }

  if (action === 'asset.bulk_estado') {
    const to = meta.to || meta.estado;
    return to ? [`Estado nuevo: ${to}${meta.count ? ` (${meta.count} activos)` : ''}`] : [];
  }
  if (action === 'asset.bulk_prioridad') {
    const to = meta.to || meta.prioridad;
    return to ? [`Prioridad nueva: ${to}${meta.count ? ` (${meta.count} activos)` : ''}`] : [];
  }

  if (action === 'asset.note.create') {
    const preview = (meta.preview || meta.texto || '').toString().trim().slice(0, 120);
    return preview ? [`«${preview}${preview.length >= 120 ? '…' : ''}»`] : [];
  }
  if (action === 'asset.review_flag') {
    const marked = !!meta.marked_for_review;
    const parts = [];
    if (marked) {
      parts.push('Solicitada revisión');
      if (Array.isArray(meta.recipient_ids_override) && meta.recipient_ids_override.length) {
        parts.push(`${meta.recipient_ids_override.length} destinatario${meta.recipient_ids_override.length === 1 ? '' : 's'}`);
      }
    } else {
      parts.push('Revisión resuelta');
    }
    if (meta.note) {
      const noteStr = String(meta.note).trim().slice(0, 120);
      parts.push(`«${noteStr}${meta.note.length > 120 ? '…' : ''}»`);
    }
    return parts;
  }
  if (action === 'asset.interaction.create') {
    const tipo = meta.tipo ? `${meta.tipo}` : 'interacción';
    const preview = (meta.preview || meta.resumen || '').toString().trim().slice(0, 100);
    return [preview ? `${tipo}: «${preview}»` : `Nueva ${tipo} registrada`];
  }
  if (action === 'asset.document.upload') {
    const lines = [];
    if (meta.nombre) lines.push(`Archivo: ${meta.nombre}`);
    if (meta.categoria) lines.push(`Categoría: ${meta.categoria}`);
    return lines;
  }
  if (action === 'asset.balance.update') {
    if (meta.changes) {
      const keys = Object.keys(meta.changes);
      return keys.slice(0, 4).map(k => describeFieldChange(k, meta.changes[k]));
    }
    return ['Balance actualizado'];
  }
  if (action === 'asset.mediation.update') {
    if (meta.changes) {
      const keys = Object.keys(meta.changes);
      return keys.slice(0, 4).map(k => describeFieldChange(k, meta.changes[k]));
    }
    return ['Mediación actualizada'];
  }

  if (action === 'document.print_preview') {
    const src = meta.source === 'print' ? 'impresión' : 'vista previa';
    return meta.nombre ? [`Documento «${meta.nombre}» abierto en ${src}`] : [`Documento abierto en ${src}`];
  }

  if (action === 'import.excel') {
    if (meta.created != null) return [`Activos creados: ${meta.created}${meta.updated != null ? ` · actualizados: ${meta.updated}` : ''}`];
    return [];
  }

  if (action === 'user.role.change') {
    return meta.from && meta.to ? [`Rol: ${meta.from} → ${meta.to}`] : [];
  }

  return [];
};

// Etiqueta cabecera ("Activo modificado", "Nota añadida", …).
export const describeChangeHeadline = (log) => {
  if (!log) return '';
  return ACTION_LABELS[log.action] || log.action || 'Cambio';
};

// Devuelve el identificador visible del activo: prioriza dirección + NPL.
export const describeAssetRef = (log) => {
  if (!log) return '';
  const npl = log.asset_codigo_npl;
  const dir = log.asset_direccion;
  if (npl && dir) return `${npl} · ${dir}`;
  if (npl) return npl;
  if (dir) return dir;
  if (log.asset_id) return `activo ${String(log.asset_id).slice(0, 8)}…`;
  return '';
};
